/**
 * DisputeModal — Phase 07
 *
 * Modal for disputing an engine review.
 * Requires reason (min 20 chars), disputed fields multi-select, and severity.
 */

import React, { useState, useMemo } from 'react';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/Button';
import CpaWorkpaperTextarea from '../../privilege/CpaWorkpaperTextarea';
import type { EngineId, DisputePayload } from '../../../types/engineReview';
import { ENGINE_NAMES } from '../../../types/engineReview';

// =============================================================================
// FIELD OPTIONS BY ENGINE
// =============================================================================

const COMMON_FIELDS = [
  { id: 'methodology', label: 'Methodology' },
  { id: 'data_sources', label: 'Data Sources' },
  { id: 'calculations', label: 'Calculations' },
  { id: 'assumptions', label: 'Assumptions' },
];

const ENGINE_SPECIFIC_FIELDS: Record<EngineId, Array<{ id: string; label: string }>> = {
  merchant_reconciliation: [
    { id: 'match_rate', label: 'Match Rate' },
    { id: 'unmatched_items', label: 'Unmatched Items' },
    { id: 'platform_coverage', label: 'Platform Coverage' },
  ],
  merchant_revenue_augmentation: [
    { id: 'augmentation_factor', label: 'Augmentation Factor' },
    { id: 'growth_trend', label: 'Growth Trend' },
  ],
  bottom_up: [
    { id: 'benchmark_period', label: 'Benchmark Period' },
    { id: 'variable_profit', label: 'Variable Profit Margin' },
    { id: 'fixed_costs', label: 'Fixed Costs Treatment' },
  ],
  enhanced_claim: [
    { id: 'growth_adjustment', label: 'Growth Adjustment' },
    { id: 'benchmark_selection', label: 'Benchmark Selection' },
  ],
  dual_method: [
    { id: 'method_comparison', label: 'Method Comparison' },
    { id: 'reconciliation', label: 'Reconciliation' },
  ],
  mitigation: [
    { id: 'mitigation_credit', label: 'Mitigation Credit' },
    { id: 'recovery_timeline', label: 'Recovery Timeline' },
  ],
  impact: [
    { id: 'impact_quantification', label: 'Impact Quantification' },
    { id: 'causation_link', label: 'Causation Link' },
  ],
  epi: [
    { id: 'epi_calculation', label: 'EPI Calculation' },
    { id: 'exclusions', label: 'Exclusions' },
  ],
  policy_deductions: [
    { id: 'deductible', label: 'Deductible' },
    { id: 'sublimits', label: 'Sublimits' },
    { id: 'coinsurance', label: 'Coinsurance' },
  ],
  pv: [
    { id: 'discount_rate', label: 'Discount Rate' },
    { id: 'pv_horizon', label: 'PV Horizon' },
  ],
  fair_settlement: [
    { id: 'settlement_range', label: 'Settlement Range' },
    { id: 'monte_carlo', label: 'Monte Carlo Parameters' },
  ],
  case_strength: [
    { id: 'strength_score', label: 'Strength Score' },
    { id: 'risk_factors', label: 'Risk Factors' },
  ],
  property: [
    { id: 'rcv_total', label: 'Replacement Cost (RCV)' },
    { id: 'acv_total', label: 'Actual Cash Value (ACV)' },
    { id: 'depreciation', label: 'Depreciation / Age-Life' },
    { id: 'coinsurance', label: 'Coinsurance / Condition of Average' },
  ],
  projection_trend: [
    { id: 'trend_factor', label: 'Trend Factor' },
    { id: 'projection_basis', label: 'Projection Basis' },
  ],
  two_period: [
    { id: 'period_selection', label: 'Period Selection' },
    { id: 'period_comparison', label: 'Period Comparison' },
  ],
  capacity_ceiling: [
    { id: 'ceiling_detection', label: 'Ceiling Detection' },
    { id: 'capacity_basis', label: 'Capacity Basis' },
  ],
  non_recurring_item_detector: [
    { id: 'flagged_items', label: 'Flagged Items' },
    { id: 'classification', label: 'Item Classification' },
  ],
  catch_up_sales: [
    { id: 'catch_up_offset', label: 'Catch-Up Offset' },
    { id: 'post_restoration_actuals', label: 'Post-Restoration Actuals' },
  ],
  monthly_limit_cap: [
    { id: 'monthly_limit', label: 'Monthly Limit (CP 15 10)' },
    { id: 'cap_application', label: 'Cap Application' },
  ],
  extra_expense_cap: [
    { id: 'ee_cap', label: 'Extra Expense Cap (CP 00 30)' },
    { id: 'three_way_minimum', label: 'Three-Way Minimum' },
  ],
  third_party_damages: [
    { id: 'elected_rows', label: 'Elected loss rows' },
    { id: 'fault_and_limit', label: 'Fault and other-party limit' },
  ],
  tort_lost_profits: [
    { id: 'but_for_basis', label: 'But-for basis' },
    { id: 'net_not_gross', label: 'Net (not gross) lost profits' },
  ],
  l_recon: [
    { id: 'doctrine', label: 'Subrogation doctrine' },
    { id: 'allocation_split', label: 'Carrier / insured allocation' },
  ],
};

// =============================================================================
// SEVERITY OPTIONS
// =============================================================================

const SEVERITY_OPTIONS: Array<{ value: DisputePayload['severity']; label: string; description: string }> = [
  {
    value: 'low',
    label: 'Low',
    description: 'Minor issue that may not significantly affect the outcome',
  },
  {
    value: 'medium',
    label: 'Medium',
    description: 'Moderate issue that could affect the calculation',
  },
  {
    value: 'high',
    label: 'High',
    description: 'Significant issue requiring attention before approval',
  },
  {
    value: 'critical',
    label: 'Critical',
    description: 'Fundamental issue that invalidates the calculation',
  },
];

// =============================================================================
// COMPONENT
// =============================================================================

export interface DisputeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: DisputePayload) => void;
  engineId: EngineId;
  isSubmitting?: boolean;
}

export default function DisputeModal({
  isOpen,
  onClose,
  onSubmit,
  engineId,
  isSubmitting = false,
}: DisputeModalProps) {
  const [reason, setReason] = useState('');
  const [disputedFields, setDisputedFields] = useState<string[]>([]);
  const [severity, setSeverity] = useState<DisputePayload['severity']>('medium');

  const engineName = ENGINE_NAMES[engineId];

  // Combine common and engine-specific fields
  const availableFields = useMemo(
    () => [...COMMON_FIELDS, ...(ENGINE_SPECIFIC_FIELDS[engineId] || [])],
    [engineId],
  );

  // Validation
  const reasonValid = reason.trim().length >= 20;
  const fieldsValid = disputedFields.length > 0;
  const canSubmit = reasonValid && fieldsValid;

  const handleFieldToggle = (fieldId: string) => {
    setDisputedFields((prev) =>
      prev.includes(fieldId)
        ? prev.filter((f) => f !== fieldId)
        : [...prev, fieldId],
    );
  };

  const handleSubmit = () => {
    onSubmit({
      reason: reason.trim(),
      disputed_fields: disputedFields,
      severity,
    });
  };

  const handleClose = () => {
    // Reset state on close
    setReason('');
    setDisputedFields([]);
    setSeverity('medium');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Dispute ${engineName}`}
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="dispute-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            data-testid="dispute-modal-submit"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Dispute'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5" data-testid="dispute-modal">
        {/* Info banner */}
        <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
            Disputing this engine will route it to the in-house accountant
            queue for review. Please provide detailed reasoning so they can
            address your concerns.
          </p>
        </div>

        {/* Reason textarea — wrapped per Phase 10 Task 10.5 */}
        <CpaWorkpaperTextarea
          id="dispute-reason"
          label={
            <>
              Reason for Dispute <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
            </>
          }
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why you are disputing this calculation (minimum 20 characters)..."
          rows={4}
          className="w-full px-3 py-2 border rounded-lg text-sm"
          style={
            reason.length > 0 && !reasonValid
              ? { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }
              : { borderColor: 'var(--color-border)' }
          }
          data-testid="dispute-modal-reason"
          helperText={
            <>
              {reason.trim().length}/20 characters minimum
              {reason.length > 0 && !reasonValid && (
                <span className="ml-2" style={{ color: 'var(--color-needs-human-text)' }}>
                  - Please provide more detail
                </span>
              )}
            </>
          }
        />

        {/* Disputed fields multi-select */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            Disputed Fields <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
          </label>
          <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
            Select which aspects of the calculation you are disputing
          </p>
          <div className="grid grid-cols-2 gap-2">
            {availableFields.map((field) => (
              <label
                key={field.id}
                className="flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors"
                style={
                  disputedFields.includes(field.id)
                    ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
                    : { backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }
                }
              >
                <input
                  type="checkbox"
                  checked={disputedFields.includes(field.id)}
                  onChange={() => handleFieldToggle(field.id)}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: 'var(--color-brand)' }}
                  data-testid={`dispute-field-${field.id}`}
                />
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{field.label}</span>
              </label>
            ))}
          </div>
          {disputedFields.length === 0 && (
            <p className="mt-1 text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
              Please select at least one field
            </p>
          )}
        </div>

        {/* Severity dropdown */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            Severity <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
          </label>
          <div className="space-y-2">
            {SEVERITY_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors"
                style={
                  severity === option.value
                    ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
                    : { backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }
                }
              >
                <input
                  type="radio"
                  name="severity"
                  value={option.value}
                  checked={severity === option.value}
                  onChange={() => setSeverity(option.value)}
                  className="mt-0.5 h-4 w-4"
                  style={{ accentColor: 'var(--color-brand)' }}
                  data-testid={`dispute-severity-${option.value}`}
                />
                <div>
                  <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {option.label}
                  </span>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

export { DisputeModal };
